import React, { useState, useMemo, useEffect } from 'react';
import './styles/theme.css';
import './shared/styles/data-table.css';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams, BrowserRouter as Router, Outlet } from 'react-router-dom';
import { CustomThemeProvider } from './ThemeContext';
import { isAuthShellPath, isPendingSecondFactorActive, isSecondFactorVerifiedSession } from './features/two-factor-auth/utils/pendingSecondFactorSession';
import { mainAuth as staffAuth } from './auth/appAuth';
import { getLocalSessionToken } from './api/authToken';
import ThemedApp from './ThemedApp';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ImpersonationProvider } from './admin-panel/impersonation/ImpersonationContext';
import { WebsiteAdminAuthProvider } from './website/context/WebsiteAdminAuthContext';
import { WebsiteThemeProvider } from './website/context/WebsiteThemeProvider';
import AppointmentDetailsPage from './manual-booking/appointment/AppointmentDetailsPage';

// Other Components
import NotFoundPage from './components/NotFoundPage';
import Login from './components/auth/Login';
import AppMagicLinkPage from './features/two-factor-auth/pages/AppMagicLinkPage';
import Register from './components/auth/Register';
import { useSessionManager } from './hooks/useSessionManager';
import AppToastHost from './components/toast/AppToastHost';
import { PageSkeleton } from './components/loading/skeletons';
import AdminHostApp from './admin-panel/routing/AdminHostApp';
import MainHostAdminRedirect from './admin-panel/routing/MainHostAdminRedirect';
import { isAdminHost, LOCAL_ADMIN_BASE_PATH, shouldRedirectLegacyAdminPaths, usesLocalAdminPath } from './admin-panel/config/adminHost';
import { ADMIN_PATHS } from './admin-panel/constants/adminPaths';
import UnifiedForgotPassword from './shared/components/auth/UnifiedForgotPassword';
// Import our custom email action handlers
import { 
  PasswordReset, 
  EmailRecovery,
  ActionLinkHandler
} from './auth/email-actions';
import VerifyEmailRedirect from './website/routes/VerifyEmailRedirect';
import { VerifyEmailPage } from './auth/verify-email/VerifyEmailPage';
import InviteAcceptPage from './auth/InviteAcceptPage';
import InviteAcceptTestPage from './auth/InviteAcceptTestPage';
import ActionLinkTestPage from './auth/email-actions/ActionLinkTestPage';
import EmailVerificationTestPage from './auth/email-actions/EmailVerificationTestPage';
import SpecialAccessInviteAcceptPage from './special-access/pages/SpecialAccessInviteAcceptPage';
import CustomPlanInviteAcceptPage from './custom-plan/pages/CustomPlanInviteAcceptPage';
import CustomPlanInviteAcceptTestPage from './custom-plan/pages/CustomPlanInviteAcceptTestPage';


// Main app sign-in uses the local session. Admin two-factor is separate.
// Website Components
import WebsiteHomePage from './website/pages/index';
import WebsitePrivacyPolicy from './website/pages/PrivacyPolicy';
import CookiePolicy from './website/pages/CookiePolicy';
import WebsiteNotFound from './website/pages/NotFound';
import WebsiteAdminLogin from './website/components/auth/WebsiteAdminLogin';
import EmployeeWebsiteRedirect from './website/components/auth/EmployeeWebsiteRedirect';
import AboutPage from './website/pages/AboutPage';
import ContactPage from './website/pages/ContactPage';
import BookDemoPage from './website/pages/BookDemoPage';
import WhyUsPage from './website/pages/why/WhyUsPage';
import TermsOfService from './website/pages/TermsOfService';
import AccountSetupPage from './website/pages/account-setup';
import TestAccountsPage from './website/dev/testaccounts/TestAccountsPage';
import { usePackageValidation } from './packages/hooks/usePackageValidation';
import PackageExpiredPage from './packages/components/PackageExpiredPage';
import PackageGracePeriodBanner from './packages/components/PackageGracePeriodBanner';
import PaymentPage from './website/pages/PaymentPage';
import BillingPage from './components/settings/pages/billing/BillingPage';
import { MaintenanceProvider, useMaintenanceMode } from './admin-panel/context/MaintenanceContext';
import MaintenancePage from './components/maintenance/MaintenancePage';
import RefundPolicy from './website/pages/RefundPolicy';
import ErrorBoundary from './components/ErrorBoundary';
import { WebsiteMaintenanceProvider, useWebsiteMaintenance } from './website/context/WebsiteMaintenanceContext';
import { ComingSoonProvider } from './coming-soon/ComingSoonContext';
import ComingSoonGuard from './coming-soon/ComingSoonGuard';
import Unauthorized from './components/Unauthorized';
import LandingPage from './customer-landing/pages/LandingPage';
import StorefrontSlugPage from './customer-landing/pages/storefront-slug/StorefrontSlugPage';
import StorefrontServicePathLegacyRedirect from './customer-landing/pages/storefront-slug/StorefrontServicePathLegacyRedirect';
import StorefrontBookAppointmentLegacyRedirect from './customer-landing/pages/storefront-slug/StorefrontBookAppointmentLegacyRedirect';
import CustomerAboutPage from './customer-landing/pages/AboutPage';
import CustomerContactPage from './customer-landing/pages/ContactPage';
import CustomerLandingLayout from './customer-landing/components/layout/CustomerLandingLayout';
import StorefrontLoginPage from './customer-landing/auth/pages/StorefrontLoginPage';
import StorefrontRegisterPage from './customer-landing/auth/pages/StorefrontRegisterPage';
import StorefrontAppointmentConfirmationPage from './customer-landing/pages/appointment-confirmation/StorefrontAppointmentConfirmationPage';
import StorefrontAppointmentCheckInPage from './customer-landing/pages/appointment-check-in/StorefrontAppointmentCheckInPage';
import StorefrontPortalDisabledPage from './customer-landing/pages/storefront-disabled/StorefrontPortalDisabledPage';
import StorefrontAuthorizeBookingSuccessPage from './customer-landing/pages/storefront-booking-success/StorefrontAuthorizeBookingSuccessPage';
import StorefrontDeluxeBookingSuccessPage from './customer-landing/pages/storefront-booking-success/StorefrontDeluxeBookingSuccessPage';
import StorefrontClientPortalRoutes from './customer-landing/storefront-client-portal/routes/StorefrontClientPortalRoutes';
import {
  StorefrontAccountLegacyRedirect,
  StorefrontAppointmentLegacyRedirect,
  StorefrontAppointmentLegacyWildcardRedirect,
} from './customer-landing/storefront-client-portal/routes/StorefrontClientPortalLegacyRedirects';
import StorefrontBookingConfirmationPage from './customer-landing/pages/storefront-booking-success/StorefrontBookingConfirmationPage';
import TestSMSPage from './sms/test/TestSMSPage';
import WorkspaceLoadingTestPage from './components/loading/workspace/WorkspaceLoadingTestPage';
import { DEV_TOOLS_PATH } from './dev-tools/constants';
import WorkspaceLoadingScreen from './components/loading/workspace/WorkspaceLoadingScreen';
import { useWorkspaceLoaderVisibility } from './components/loading/workspace/useMinimumVisibleLoading';
import AppImpersonateLandingPage from './impersonation/pages/AppImpersonateLandingPage';
import { isImpersonationSessionActive, isImpersonationExiting, isImpersonationEntering, getSignedImpersonationSession, clearStaleImpersonationExit } from './impersonation/utils/impersonationSession';

const AppIndexRedirect = () => {
  const { user, loading } = useAuth();

  if (isImpersonationEntering()) {
    return <Navigate to="/app/impersonate" replace />;
  }

  if (isImpersonationExiting() || (isImpersonationSessionActive() && (loading || !user))) {
    return (
      <WorkspaceLoadingScreen
        message="Signing into this account..."
        detail="Secure admin access · skipping customer 2FA"
      />
    );
  }

  if (isPendingSecondFactorActive()) {
    return <Navigate to="/app/login" replace state={{ resumeAppSecondFactor: true }} />;
  }

  if (loading && !user) {
    return <WorkspaceLoadingScreen message="Preparing your workspace..." />;
  }

  if (user) {
    const hasVerifiedAppSession = user.uid && isSecondFactorVerifiedSession(user.uid);
    if (!hasVerifiedAppSession && !getSignedImpersonationSession()) {
      return <Navigate to="/app/login" replace state={{ resumeAppSecondFactor: true }} />;
    }

    return <Navigate to="/app/overview" replace />;
  }

  return <Navigate to="/app/login" replace />;
};

const WORKSPACE_PREP_TIMEOUT_MS = 8000;
const WORKSPACE_PREP_WITH_SESSION_TIMEOUT_MS = 45000;

const PrivateRoute = ({ children, skipPackageCheck = false, requireOwner = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const packageValidationEnabled = !!user;
  const { isLoading, hasPackage, status } = usePackageValidation(user?.companyId, packageValidationEnabled);
  const { isMaintenanceMode, scheduledStartTime, loading: maintenanceLoading } = useMaintenanceMode();
  const [reloadTimerStarted, setReloadTimerStarted] = useState(false);
  const [showFinalizeScreen, setShowFinalizeScreen] = useState(false);

  // Only force a login redirect when we still do not have an authenticated user.
  // Authenticated users can legitimately spend longer here while company/package
  // data hydrates, and bouncing them back to login creates a false "logout".
  const isPrepStuck = !user && (loading || isLoading || maintenanceLoading);
  useEffect(() => {
    if (!isPrepStuck) return;
    const hasLocalSession = Boolean(getLocalSessionToken());
    const hasStaffSession =
      Boolean(staffAuth?.currentUser) ||
      isSecondFactorVerifiedSession(staffAuth?.currentUser?.uid) ||
      hasLocalSession;
    const timeoutMs = hasStaffSession
      ? WORKSPACE_PREP_WITH_SESSION_TIMEOUT_MS
      : WORKSPACE_PREP_TIMEOUT_MS;
    const timer = setTimeout(() => {
      // Do not treat a slow refresh as a logout when a session is still present.
      if (
        staffAuth?.currentUser ||
        isSecondFactorVerifiedSession(staffAuth?.currentUser?.uid) ||
        getLocalSessionToken()
      ) {
        return;
      }
      navigate('/app/login', { replace: true });
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [isPrepStuck, navigate]);

  // One-time reload if account finalization hangs (e.g., custom claims not visible yet)
  useEffect(() => {
    if (user && !user.companyId && !reloadTimerStarted) {
      const alreadyReloaded = sessionStorage.getItem('finalizeReloaded') === 'true';
      if (!alreadyReloaded) {
        setReloadTimerStarted(true);
        const timer = setTimeout(() => {
          try { sessionStorage.setItem('finalizeReloaded', 'true'); } catch (_) {}
          // Use replace so we don't stack history
          window.location.replace(window.location.href);
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [user, reloadTimerStarted]);

  // Debounce the finalizing screen to avoid brief flashes
  useEffect(() => {
    if (user && !user.companyId) {
      const timer = setTimeout(() => setShowFinalizeScreen(true), 600);
      return () => {
        clearTimeout(timer);
        setShowFinalizeScreen(false);
      };
    } else {
      setShowFinalizeScreen(false);
    }
  }, [user]);

  // Check if user is an admin
  const isAdmin = user?.role === 'admin' || user?.isAdmin;

  const authLoading = loading;
  const hasWorkspace = Boolean(user);
  const workspaceLoading =
    hasWorkspace &&
    (maintenanceLoading ||
      (packageValidationEnabled && (isLoading || status === null)));
  const loaderActive = hasWorkspace && (authLoading || workspaceLoading);
  const showLoader = useWorkspaceLoaderVisibility(loaderActive);

  if (showLoader) {
    return <WorkspaceLoadingScreen message="Preparing your workspace..." />;
  }

  if (isImpersonationExiting()) {
    return <WorkspaceLoadingScreen message="Returning to admin panel..." />;
  }

  if (isImpersonationEntering() || (isImpersonationSessionActive() && (!user || loading))) {
    return (
      <WorkspaceLoadingScreen
        message="Signing into this account..."
        detail="Secure admin access · skipping customer 2FA"
      />
    );
  }

  if (authLoading && !user) {
    if (isPendingSecondFactorActive()) {
      return (
        <Navigate
          to="/app/login"
          replace
          state={{ resumeAppSecondFactor: true, from: location }}
        />
      );
    }
    return <WorkspaceLoadingScreen message="Preparing your workspace..." />;
  }

  if (!user) {
    if (!loading && staffAuth?.currentUser) {
      return (
        <Navigate
          to="/app/login"
          replace
          state={{ resumeAppSecondFactor: true, from: location }}
        />
      );
    }
    return <Navigate to="/app/login" replace />;
  }

  if (
    user.uid &&
    !isSecondFactorVerifiedSession(user.uid) &&
    !getSignedImpersonationSession()
  ) {
    return (
      <Navigate
        to="/app/login"
        replace
        state={{ resumeAppSecondFactor: true, from: location }}
      />
    );
  }

  // Allow admins to bypass maintenance mode
  if (isMaintenanceMode && !isAdmin) {
    return <MaintenancePage variant="app" scheduledStartTime={scheduledStartTime} />;
  }


  if (!user.companyId) {
    return showFinalizeScreen ? (
      <WorkspaceLoadingScreen message="Finalizing your account setup..." />
    ) : null;
  }

  if (requireOwner && String(user.role || '').toLowerCase() !== 'owner') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (skipPackageCheck) {
    return children;
  }

  // If company has no package, redirect to expired page
  if (!hasPackage) {
    return <Navigate to="/app/package-expired" replace />;
  }

  // Allow access during trial or grace period (soft landing)
  if (status === 'trial' || status === 'grace_period') {
    return (
      <>
        {status === 'grace_period' ? <PackageGracePeriodBanner /> : null}
        {children}
      </>
    );
  }

  if (status === 'expired') {
    return <Navigate to="/app/package-expired" replace />;
  }

  if (status === 'cancelled') {
    return children;
  }

  if (!['active', 'trial', 'grace_period'].includes(status)) {
    return <Navigate to="/app/package-expired" replace />;
  }

  return children;
};

const RouteLogger = () => {
  const location = useLocation();

  useEffect(() => {
    // Send GA4 page_view on route changes if gtag is available
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        const pagePath = `${location.pathname}${location.search || ''}`;
        const pageLocation = typeof window !== 'undefined' ? window.location.href : undefined;
        const pageTitle = typeof document !== 'undefined' ? document.title : undefined;
        window.gtag('event', 'page_view', {
          page_path: pagePath,
          page_location: pageLocation,
          page_title: pageTitle,
        });
      }
    } catch {}
  }, [location]);

  return null;
};

// Create a wrapper component for website pages
const WebsitePageWrapper = ({ children }) => {
  return (
    <WebsiteThemeProvider>
      {children}
    </WebsiteThemeProvider>
  );
};

const WebsiteMaintenanceGuard = ({ children }) => {
  const { isWebsiteMaintenance, loading } = useWebsiteMaintenance();
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Check if user is an admin
  const isAdmin = user?.role === 'admin' || user?.isAdmin;
  const isWebsiteHome = pathname === '/';

  if (loading && !isWebsiteHome) {
    return <PageSkeleton fill label="Loading" />;
  }

  // Allow admins to bypass maintenance mode
  if (!loading && isWebsiteMaintenance && !isAdmin) {
    return <MaintenancePage variant="website" />;
  }

  return children;
};

const LegacyEmployeeProfileRedirect = () => {
  const { employeeId } = useParams();
  return <Navigate to={`/app/overview/employees/profile/${employeeId}`} replace />;
};

function App() {
  if (isAdminHost()) {
    return <AdminHostApp />;
  }

  clearStaleImpersonationExit();

  const redirectLegacyAdmin = shouldRedirectLegacyAdminPaths();
  const localAdmin = usesLocalAdminPath();

  return (
    <AuthProvider>
      <NotificationProvider>
        <CustomThemeProvider>
          <WebsiteAdminAuthProvider>
            <Routes>
              {localAdmin ? (
                <Route path={`${LOCAL_ADMIN_BASE_PATH}/*`} element={<AdminHostApp />} />
              ) : null}
              {redirectLegacyAdmin ? (
                <Route
                  path="/support-login"
                  element={<MainHostAdminRedirect path={ADMIN_PATHS.SUPPORT_LOGIN} />}
                />
              ) : null}

              <Route
                path="/*"
                element={
                  <MaintenanceProvider>
                    <WebsiteMaintenanceProvider>
                      <ComingSoonProvider>
                        <AppContent />
                      </ComingSoonProvider>
                    </WebsiteMaintenanceProvider>
                  </MaintenanceProvider>
                }
              />
            </Routes>
            <AppToastHost />
          </WebsiteAdminAuthProvider>
        </CustomThemeProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

function AppContent() {
  useSessionManager();
  const location = useLocation();
  const isAuthShell = isAuthShellPath(location.pathname);

  // Force scroll to top on route changes for website routes (excluding hash navigation)
  useEffect(() => {
    if (!location.hash) {
      // Next frame to ensure layout is ready
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    }
  }, [location.pathname]);

  return (
    <>
      {!isAuthShell ? <CssBaseline /> : null}
      <RouteLogger />
      <ComingSoonGuard>
      <Routes>
        {/* Main Website Routes - wrapped so website maintenance blocks these paths */}
        <Route path="/" element={<WebsiteMaintenanceGuard><EmployeeWebsiteRedirect><Outlet /></EmployeeWebsiteRedirect></WebsiteMaintenanceGuard>}>
          <Route path="unauthorized" element={<Navigate to="/app/unauthorized" replace />} />
          <Route index element={<WebsitePageWrapper><WebsiteHomePage /></WebsitePageWrapper>} />
            <Route path="main">
              <Route path="admin-login" element={<WebsitePageWrapper><WebsiteAdminLogin /></WebsitePageWrapper>} />
            <Route path="privacy-policy" element={<WebsitePageWrapper><WebsitePrivacyPolicy /></WebsitePageWrapper>} />
            <Route path="cookie-policy" element={<WebsitePageWrapper><CookiePolicy /></WebsitePageWrapper>} />
            <Route path="terms" element={<WebsitePageWrapper><TermsOfService /></WebsitePageWrapper>} />
            <Route path="refund-policy" element={<WebsitePageWrapper><RefundPolicy /></WebsitePageWrapper>} />
            <Route path="account-setup" element={<WebsitePageWrapper><AccountSetupPage /></WebsitePageWrapper>} />
            <Route path="custom-plan/accept" element={<WebsitePageWrapper><CustomPlanInviteAcceptPage /></WebsitePageWrapper>} />
            <Route path="verify-email" element={<VerifyEmailRedirect />} />
            <Route path="payment" element={<WebsitePageWrapper><PaymentPage /></WebsitePageWrapper>} />
          </Route>

          {/* Add redirect for direct privacy policy access */}
          <Route path="privacy-policy" element={<Navigate to="/main/privacy-policy" replace />} />
          
          {/* New Website Routes */}
          <Route path="about" element={<WebsitePageWrapper><AboutPage /></WebsitePageWrapper>} />
          <Route path="contact" element={<WebsitePageWrapper><ContactPage /></WebsitePageWrapper>} />
          <Route path="book-demo" element={<WebsitePageWrapper><BookDemoPage /></WebsitePageWrapper>} />
          <Route path="why" element={<WebsitePageWrapper><WhyUsPage /></WebsitePageWrapper>} />
          {/* Map direct email action routes to /app counterparts to support continueUrl links */}
          <Route path="verify-email" element={<VerifyEmailRedirect />} />
          <Route path="recover-email" element={<Navigate to="/app/recover-email" replace />} />
          <Route path="reset-password" element={<Navigate to="/app/reset-password" replace />} />
          {/* Catch auth action links at the root as well */}
          <Route path="__/auth/action" element={<ActionLinkHandler />} />
        </Route>

        {/* Customer Landing Page - Public Booking Page (website maintenance applies here too) */}
        <Route path="/book" element={<WebsiteMaintenanceGuard><Outlet /></WebsiteMaintenanceGuard>}>
          <Route path=":companyUrl" element={<CustomerLandingLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="login" element={<StorefrontLoginPage />} />
            <Route path="register" element={<StorefrontRegisterPage />} />
            <Route path="confirm/:token" element={<StorefrontAppointmentConfirmationPage />} />
            <Route path="check-in/:token" element={<StorefrontAppointmentCheckInPage />} />
            <Route path="disabled" element={<StorefrontPortalDisabledPage />} />
            <Route path="service/:serviceSlug" element={<StorefrontServicePathLegacyRedirect />} />
            <Route
              path="book-appointment/:serviceSlug?"
              element={<StorefrontBookAppointmentLegacyRedirect />}
            />
            <Route
              path="booking/success/deposit/authorize/:appointmentId"
              element={<StorefrontAuthorizeBookingSuccessPage />}
            />
            <Route
              path="booking/success/balance/authorize/:appointmentId"
              element={<StorefrontAuthorizeBookingSuccessPage />}
            />
            <Route
              path="booking/success/payment/authorize/:appointmentId"
              element={<StorefrontAuthorizeBookingSuccessPage />}
            />
            <Route
              path="booking/success/deposit/deluxe/:appointmentId"
              element={<StorefrontDeluxeBookingSuccessPage />}
            />
            <Route
              path="booking/success/balance/deluxe/:appointmentId"
              element={<StorefrontDeluxeBookingSuccessPage />}
            />
            <Route
              path="booking/success/payment/deluxe/:appointmentId"
              element={<StorefrontDeluxeBookingSuccessPage />}
            />
            <Route
              path="booking/confirmed/:appointmentId"
              element={<StorefrontBookingConfirmationPage />}
            />
            <Route path="account/*" element={<StorefrontClientPortalRoutes />} />
            <Route path="appointments/:appointmentId/*" element={<StorefrontAppointmentLegacyWildcardRedirect />} />
            <Route path="appointments/:appointmentId" element={<StorefrontAppointmentLegacyRedirect />} />
            <Route path="appointment/:appointmentId/*" element={<StorefrontAppointmentLegacyWildcardRedirect />} />
            <Route path="appointment/:appointmentId" element={<StorefrontAppointmentLegacyRedirect />} />
            <Route path="portal/*" element={<StorefrontAccountLegacyRedirect />} />
            <Route path="about" element={<CustomerAboutPage />} />
            <Route path="contact" element={<CustomerContactPage />} />
            <Route path=":storefrontSlug" element={<StorefrontSlugPage />} />
          </Route>
        </Route>

        {/* App Routes */}
        <Route path="/app">
          {/* Intelligent redirect for /app/: to login if unauthenticated, overview if authenticated */}
          <Route index element={<AppIndexRedirect />} />
          <Route path="unauthorized" element={<Unauthorized />} />

          {/* Package Routes */}
          <Route path="package-expired" element={<PackageExpiredPage />} />
          <Route path="package-grace-period" element={<PackageExpiredPage isGracePeriod={true} />} />
          <Route
            path="billing"
            element={
              <PrivateRoute skipPackageCheck requireOwner>
                <BillingPage />
              </PrivateRoute>
            }
          />

          {/* Employee Profile Routes */}
          <Route 
            path="employees/profile/:employeeId" 
            element={
              <PrivateRoute>
                <LegacyEmployeeProfileRedirect />
              </PrivateRoute>
            } 
          />

          {/* App Auth Routes */}
          <Route path="login" element={<Login />} />
          <Route path="impersonate" element={<AppImpersonateLandingPage />} />
          <Route path="magic-link" element={<AppMagicLinkPage />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<UnifiedForgotPassword />} />
          <Route path="invite/accept" element={<InviteAcceptPage />} />
          <Route path="special-access/accept" element={<SpecialAccessInviteAcceptPage />} />
          <Route path="invite/accept-test" element={<InviteAcceptTestPage />} />
          
          {/* Custom Email Action Handler Routes */}
          <Route path="reset-password" element={<PasswordReset />} />
          <Route path="verify-email" element={<VerifyEmailPage />} />
          <Route path="recover-email" element={<EmailRecovery />} />
          <Route path="__/auth/action" element={<ActionLinkHandler />} />
          <Route path="test/action-link" element={<ActionLinkTestPage />} />
          <Route path="test/verify-email" element={<EmailVerificationTestPage />} />
          <Route path="test/workspace-loading" element={<WorkspaceLoadingTestPage />} />
          <Route path="dev/tools" element={<Navigate to={DEV_TOOLS_PATH} replace />} />
          <Route path="test/sms" element={<PrivateRoute><TestSMSPage /></PrivateRoute>} />
          

          
          
  

          
          {/* App Main Routes */}
          <Route path="overview/*" element={
            <PrivateRoute>
              <ImpersonationProvider>
                <ThemedApp />
              </ImpersonationProvider>
            </PrivateRoute>
          } />

          {/* Other App Routes */}
          <Route path="portal-disabled" element={<StorefrontPortalDisabledPage />} />
          <Route path="*" element={<Navigate to="/app/overview" replace />} />

        </Route>

        {/* Dev Routes */}
        <Route path="/dev">
          <Route path="testaccounts" element={<WebsitePageWrapper><TestAccountsPage /></WebsitePageWrapper>} />
          <Route path="custom-plan-invite" element={<WebsitePageWrapper><CustomPlanInviteAcceptTestPage /></WebsitePageWrapper>} />
        </Route>
        {/* 404 Routes */}
        <Route path="*" element={<WebsiteNotFound />} />
      </Routes>
      </ComingSoonGuard>
    </>
  );
}

export default App;